import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Space, Alert, message } from 'antd';
import api from '../../../services/api';
import { load as loadMpSdk, isConfigured } from '../../../utils/mpSdk';

// Traducao dos status_detail mais comuns do MP pra algo que o organizador
// entende sem precisar de google. NUNCA loga os dados do cartao — so o
// status_detail (codigo generico, sem dado sensivel) que ja vem do MP.
const REJECTION_MESSAGES = {
  cc_rejected_insufficient_amount: 'saldo insuficiente',
  cc_rejected_bad_filled_card_number: 'número do cartão inválido',
  cc_rejected_bad_filled_date: 'data de validade inválida',
  cc_rejected_bad_filled_security_code: 'código de segurança (CVV) inválido',
  cc_rejected_bad_filled_other: 'dados do cartão inválidos, confere e tenta de novo',
  cc_rejected_call_for_authorize: 'cartão exige autorização, liga pro banco antes de tentar de novo',
  cc_rejected_card_disabled: 'cartão desabilitado, liga pro banco pra ativar',
  cc_rejected_duplicated_payment: 'já existe um pagamento igual em andamento',
  cc_rejected_high_risk: 'pagamento recusado por segurança pelo banco',
  cc_rejected_max_attempts: 'muitas tentativas com esse cartão, tenta mais tarde',
  cc_rejected_other_reason: 'cartão recusado pelo banco, sem motivo detalhado',
  cc_rejected_invalid_installments: 'parcelamento inválido',
  cc_rejected_card_type_not_allowed: 'esse tipo de cartão não é aceito',
};

function friendlyRejection(statusDetail) {
  if (!statusDetail) return 'cartão recusado pelo banco';
  return REJECTION_MESSAGES[statusDetail] || `cartão recusado (${statusDetail})`;
}

function formatCardNumber(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatExpiry(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

// Form de pagamento com cartao: tokeniza os dados direto no SDK.js do
// Mercado Pago (numero/cvv/validade nunca tocam nosso backend, so o token
// gerado no navegador do usuario) e manda o token + CPF pro backend criar
// a cobranca.
// props: { amount, packKey, teamId, onPaid, onCancel, onSubmittingChange, registerExpense }
export default function CardPaymentPanel({ amount, packKey, teamId, onPaid, onCancel, onSubmittingChange, registerExpense = false }) {
  const [form] = Form.useForm();
  const [mp, setMp] = useState(null);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(null); // motivo de indisponibilidade (bloqueia o form)
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Lock sincrono contra double-click: setState e assincrono, entao 2 clicks
  // em <16ms passam pelo mesmo `submitting` false e disparam 2 tokens/cobrancas
  // reais no MP. O ref trava na hora, antes de qualquer await.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!isConfigured()) {
      setUnavailable('Pagamento com cartão não tá configurado ainda. Usa Pix ou fala com o suporte.');
      return;
    }
    let cancelled = false;
    loadMpSdk()
      .then((instance) => {
        if (cancelled) return;
        setMp(instance);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setUnavailable('Não deu pra carregar o pagamento com cartão agora. Usa Pix ou tenta de novo em instantes.');
      });
    return () => { cancelled = true; };
  }, []);

  const submit = async (values) => {
    if (!mp) return;
    if (submittingRef.current) return; // ja tem uma cobranca em voo, ignora
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    onSubmittingChange?.(true); // avisa o modal pai pra travar o fechamento
    try {
      const cpfDigits = values.cpf.replace(/\D/g, '');
      const [expMonth, expYear] = values.expiry.split('/');

      // 1. Tokeniza o cartao direto no SDK do MP (PCI-safe: numero, cvv e
      // validade nao saem do navegador do usuario, so o token abaixo).
      const cardToken = await mp.createCardToken({
        cardNumber: values.card_number.replace(/\s/g, ''),
        cardholderName: values.holder_name.trim(),
        cardExpirationMonth: expMonth,
        cardExpirationYear: `20${expYear}`,
        securityCode: values.cvv,
        identificationType: 'CPF',
        identificationNumber: cpfDigits,
      });

      if (!cardToken || !cardToken.id) {
        setSubmitError('Não deu pra validar os dados do cartão. Confere e tenta de novo.');
        return;
      }

      // 2. So o token (nunca os dados do cartao) vai pro nosso backend.
      const r = await api.post('/community/finance/credits/purchase/card', {
        community_team_id: teamId,
        pack_key: packKey,
        card_token: cardToken.id,
        payer_cpf: cpfDigits,
        register_expense: registerExpense,
      });

      if (r.data.status === 'paid') {
        message.success('Cartão aprovado! Créditos já caíram no seu saldo.');
        onPaid?.(r.data);
      } else if (r.data.status === 'pending') {
        message.info('Pagamento em análise, já te avisamos assim que aprovar.');
        onPaid?.(r.data); // fecha o modal, saldo atualiza depois via webhook
      } else {
        setSubmitError(`Cartão recusado: ${friendlyRejection(r.data.status_detail)}`);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 402) {
        setSubmitError(`Cartão recusado: ${friendlyRejection(data?.status_detail)}`);
      } else if (status === 502) {
        setSubmitError('Problema no processamento. Tenta de novo.');
      } else if (status === 503) {
        setSubmitError('Cartão não configurado. Fala com o suporte.');
      } else if (err.name === 'Error' && !err.response) {
        // Erro do proprio SDK do MP na tokenizacao (dados invalidos etc).
        setSubmitError(err.message || 'Não deu pra validar os dados do cartão. Confere e tenta de novo.');
      } else {
        setSubmitError(data?.message || 'Falha ao processar o pagamento. Tenta de novo.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  if (unavailable) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert type="warning" showIcon message="Cartão indisponível" description={unavailable} />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>Voltar</Button>
        </Space>
      </Space>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={submit} disabled={!ready || submitting}>
      <Form.Item
        label="Nome no cartão"
        name="holder_name"
        rules={[{ required: true, message: 'obrigatório' }]}
      >
        <Input placeholder="Como está escrito no cartão" autoComplete="cc-name" />
      </Form.Item>

      <Form.Item
        label="Número do cartão"
        name="card_number"
        normalize={formatCardNumber}
        rules={[
          { required: true, message: 'obrigatório' },
          { pattern: /^\d{4} \d{4} \d{4} \d{3,4}$/, message: 'número inválido' },
        ]}
      >
        <Input placeholder="0000 0000 0000 0000" maxLength={19} inputMode="numeric" autoComplete="cc-number" />
      </Form.Item>

      <Space.Compact block>
        <Form.Item
          label="Validade (MM/AA)"
          name="expiry"
          normalize={formatExpiry}
          rules={[
            { required: true, message: 'obrigatório' },
            { pattern: /^(0[1-9]|1[0-2])\/\d{2}$/, message: 'formato MM/AA' },
          ]}
          style={{ width: '50%' }}
        >
          <Input placeholder="MM/AA" maxLength={5} inputMode="numeric" autoComplete="cc-exp" />
        </Form.Item>
        <Form.Item
          label="CVV"
          name="cvv"
          rules={[
            { required: true, message: 'obrigatório' },
            { pattern: /^\d{3,4}$/, message: '3 ou 4 dígitos' },
          ]}
          style={{ width: '50%' }}
        >
          <Input placeholder="000" maxLength={4} inputMode="numeric" autoComplete="cc-csc" />
        </Form.Item>
      </Space.Compact>

      <Form.Item
        label="CPF do titular"
        name="cpf"
        normalize={(v) => v.replace(/\D/g, '').slice(0, 11)}
        rules={[
          { required: true, message: 'obrigatório' },
          { len: 11, message: 'CPF precisa ter 11 dígitos' },
        ]}
      >
        <Input placeholder="Só números" maxLength={11} inputMode="numeric" />
      </Form.Item>

      {submitError && (
        <Alert type="error" showIcon message={submitError} style={{ marginBottom: 16 }} />
      )}

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={submitting}>Voltar</Button>
        <Button type="primary" htmlType="submit" loading={submitting} disabled={!ready}>
          Pagar R$ {Number(amount || 0).toFixed(2).replace('.', ',')}
        </Button>
      </Space>
    </Form>
  );
}
